import { AirToPremiereEvent, PremiereToAirEvent, type PremiereToAirMessage } from '@air/premiere-constants';

/**
 * `sendPluginHostMessage` is intentionally reimplemented here rather than
 * imported from `@air/plugin-ui`. The shared helper only speaks the
 * `window.parent.postMessage` iframe protocol, which is what Sanity (a
 * standard browser iframe plugin) needs. Premiere's WebView runs inside
 * Adobe UXP where the message bridge is `window.uxpHost.postMessage` — a
 * different native channel.
 *
 * We evaluated lifting UXP support into `@air/plugin-ui` (2026-04-19) but
 * opted against it: only one UXP consumer exists today, the divergence is
 * architectural not boilerplate, and a cross-plugin refactor would pull
 * this branch out of its scope. Revisit when a second UXP plugin (e.g.,
 * After Effects, Photoshop) lands.
 */

/**
 * Send a message from the WebView to the UXP plugin host.
 * Uses `window.uxpHost.postMessage()` in UXP, falls back to `window.parent.postMessage()` in iframes.
 */
export function sendPluginHostMessage<T extends { type: string }>(message: T): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.uxpHost) {
    window.uxpHost.postMessage(message);
    return;
  }
  // Fallback for iframe-based dev (e.g. browser testing)
  if (window.parent !== window) {
    window.parent.postMessage(message, '*');
  }
}

export const sendReadyMessage = (): void => {
  sendPluginHostMessage({ type: AirToPremiereEvent.Ready });
};

const PREMIERE_TO_AIR_EVENT_TYPES = new Set<string>(Object.values(PremiereToAirEvent));

/**
 * Type guard for messages from the UXP host to the WebView.
 * Validates that `type` is one of the registered PremiereToAirEvent values so
 * downstream switch-based dispatch can rely on the discriminant without a cast.
 */
export const isPremiereToAirMessage = (value: unknown): value is PremiereToAirMessage => {
  if (typeof value !== 'object' || value === null || !('type' in value)) {
    return false;
  }
  const { type } = value;

  return typeof type === 'string' && PREMIERE_TO_AIR_EVENT_TYPES.has(type);
};
